import {
  differenceInDays,
  differenceInHours,
  formatDistance,
  formatDistanceStrict,
  isToday,
  isTomorrow,
  isValid,
  isYesterday,
  parseISO,
} from "date-fns";
import { format, toZonedTime } from "date-fns-tz";

export const formatUserTimeZone = ({
  timeZone,
  isDate = false,
}: {
  timeZone: string;
  isDate?: boolean;
}) => {
  const zonedDate = toZonedTime(new Date(), timeZone);

  const newDate = format(zonedDate, "dd MMM yyyy, hh:mm a", { timeZone });
  const newTime = format(zonedDate, "hh:mm a", { timeZone });
  return isDate ? newDate : newTime;
};

export const formatTimeZoneWithGMT = (timeZone: string): string => {
  try {
    const now = new Date();

    // Get GMT Offset
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });

    const parts = formatter.formatToParts(now);
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    const gmtOffset = tzPart?.value?.replace("GMT", "GMT ") ?? "";

    // Extract country name from timezone
    const country = timeZone.split("/")[1]?.replace("_", " ") ?? timeZone;

    return `${country} (${gmtOffset})`;
  } catch (error) {
    return timeZone;
  }
};

export const getDefaultHeaders = () => {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  };
};

export const removeBlankProperties = (obj: object) => {
  return JSON.parse(JSON.stringify(obj, replaceUndefinedOrNull));
};

export function replaceUndefinedOrNull(key, value) {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  return value;
}

/**
 * returns logged in user is
 * @param
 */

export function formatDuration(duration) {
  if (parseFloat(duration) > 0) {
    return (parseFloat(duration) / 60).toFixed(2) + " min";
  }
  return "-";
}

type DateFormat =
  | "onlyDate"
  | "datetime"
  | "noYearDatetime"
  | "auditLog"
  | "datetimeWithAt"
  | "threadCard"
  | "onlyTime";

export const dateFormatter = (
  unformattedDate: Date | number | string | null | undefined,
  formatType: DateFormat = "onlyDate", // Set auditLog as default for your activity feed
) => {
  if (!unformattedDate) return "-";

  const dateObj =
    unformattedDate instanceof Date
      ? unformattedDate
      : new Date(unformattedDate);

  if (isNaN(dateObj.getTime())) return "-";

  const now = new Date();

  // Logic for Thread Card ("Today at 10:15 am" style)
  if (formatType === "threadCard") {
    const diffInDays = Math.abs(differenceInDays(now, dateObj));

    if (isToday(dateObj)) {
      return `Today at ${format(dateObj, "h:mm a")}`;
    }

    if (isYesterday(dateObj)) {
      return `Yesterday at ${format(dateObj, "h:mm a")}`;
    }

    if (diffInDays < 7) {
      return format(dateObj, "EEEE 'at' h:mm a"); // e.g. "Monday at 10:15 am"
    }

    return format(dateObj, "d MMM 'at' h:mm a"); // e.g. "3 Sep at 2:15 pm"
  }

  // Logic for Audit Logs (Within 1 week)
  if (formatType === "auditLog") {
    const diffInHours = Math.abs(differenceInHours(now, dateObj));
    const diffInDays = Math.abs(differenceInDays(now, dateObj));

    // 1. Within the last 24 hours (e.g., "15 min ago", "2 hours ago")
    if (diffInHours < 24) {
      return formatDistanceStrict(dateObj, now, {
        addSuffix: true,
      });
    }

    // 2. Yesterday (e.g., "Yesterday, 10:15 am")
    if (isYesterday(dateObj)) {
      return `Yesterday, ${format(dateObj, "h:mm a")}`;
    }

    // 3. Within 7 days (e.g., "3 days ago")
    if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    }

    // 4. Older than 1 week (e.g., "3 Sep, 2:15 pm")
    return format(dateObj, "d MMM, h:mm a");
  }

  // Standard formatting logic
  switch (formatType) {
    case "onlyDate":
      return format(dateObj, "dd MMM, yyyy");

    case "noYearDatetime":
      return format(dateObj, "MMM dd, hh:mm a");

    case "onlyTime":
      return format(dateObj, "h:mm aaa");

    case "datetimeWithAt":
      return format(dateObj, "MMM d, yyyy 'at' h:mm a");

    case "datetime":
    default:
      return format(dateObj, "MMM dd, yyyy hh:mm a");
  }
};
export const dateCreatedFormatter = (unformattedDate) => {
  if (!unformattedDate) {
    return "-";
  }
  let formatDate = new Date(unformattedDate);
  const newDate = format(formatDate, "MMM dd");
  return newDate;
};

export const randomString = (length) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export function getInitials(name?: string): string {
  if (!name) return ""; // Fallback for undefined or empty names
  const parts = name.split(" ");
  const initials = parts.map((part) => part[0]?.toUpperCase() || "").join("");
  return initials.substring(0, 1); // Return up to 1 initials
}

export const formatDateDistance = (date) => {
  if (!date) {
    return "-";
  }
  return formatDistance(new Date(date), new Date(), {
    addSuffix: true,
  });
};

export const createTransparentColor = (hex, alpha = 0.2) => {
  let r = 0,
    g = 0,
    b = 0;

  // If shorthand like "#f00"
  if (hex?.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // If full like "#ff0000"
  else if (hex?.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const groupCreationDateFormatter = (creationDate: string) => {
  if (!creationDate) {
    return "-";
  }
  let formatDate = new Date(creationDate);
  const newDate = format(formatDate, "dd MMM, yyyy");
  return newDate;
};

export const getDueDateLabel = (id?: string) => {
  if (!id) return "No due date";

  if (id === "Future" || id === "Past due dates") {
    return id;
  }

  const date = parseISO(id);

  if (!isValid(date)) return "No due date";

  if (isToday(date)) return "Today";

  if (isTomorrow(date)) return "Tomorrow";

  return format(date, "EEEE");
};
